import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const EDITION_LABELS = {
  express: "XProtect Express+",
  professional: "XProtect Professional+",
  expert: "XProtect Expert",
  corporate: "XProtect Corporate",
};

const ACCENT = [22, 104, 182];
const ACCENT_SOFT = [235, 245, 255];
const SLATE = [80, 96, 112];
const DARK = [11, 18, 32];
const MUTED = [110, 110, 110];
const BORDER = [220, 226, 232];

const MARGIN_X = 40;
const PAGE_BOTTOM_LIMIT = 780;

let LOGO_DATA_URL = null;
async function loadLogo() {
  if (LOGO_DATA_URL) return LOGO_DATA_URL;
  try {
    const res = await fetch("/xortec/xortec-logo.png");
    const blob = await res.blob();
    LOGO_DATA_URL = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
    return LOGO_DATA_URL;
  } catch { return null; }
}

// Standard jsPDF helvetica-Font supports WinAnsi/Latin-1, not multi-byte unicode.
// We normalise unicode symbols + special chars to safe ASCII/Latin-1 equivalents
// to prevent broken glyph spacing.
function ascii(str) {
  if (str == null) return "";
  return String(str)
    .replace(/[\u2713\u2714]/g, "+")
    .replace(/[\u2717\u2718]/g, "x")
    .replace(/[\u2022\u25CF]/g, "-")
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB]/g, '"')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u2192\u2794]/g, "->")
    .replace(/[\u2605\u2606]/g, "*")
    .replace(/\u00A0/g, " ");
}

function ensureSpace(doc, y, needed = 40) {
  if (y + needed > PAGE_BOTTOM_LIMIT) { addPageWithHeader(doc); return 90; }
  return y;
}

function addPageWithHeader(doc) {
  doc.addPage();
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setDrawColor(...ACCENT); doc.setLineWidth(1);
  doc.line(MARGIN_X, 60, pageWidth - MARGIN_X, 60);
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED);
  doc.text("Milestone XProtect Empfehlung - Xortec GmbH", MARGIN_X, 50);
}

function sectionTitle(doc, title, y) {
  y = ensureSpace(doc, y, 40);
  doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.setTextColor(...ACCENT);
  doc.text(ascii(title), MARGIN_X, y);
  doc.setDrawColor(...ACCENT); doc.setLineWidth(0.8);
  doc.line(MARGIN_X, y + 4, MARGIN_X + 40, y + 4);
  return y + 16;
}

export async function exportPdf({ projectName, answers, recommendation, features, questions, cart, configuration, configCode }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN_X * 2;

  // Header
  const logo = await loadLogo();
  if (logo) {
    try { doc.addImage(logo, "PNG", MARGIN_X, 30, 100, 28, undefined, "FAST"); } catch {}
  } else {
    doc.setFillColor(...ACCENT); doc.rect(MARGIN_X, 30, 80, 28, "F");
    doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
    doc.text("XORTEC", MARGIN_X + 40, 49, { align: "center" });
  }

  doc.setDrawColor(...ACCENT); doc.setLineWidth(2);
  doc.line(MARGIN_X, 70, pageWidth - MARGIN_X, 70);

  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...DARK);
  doc.text("Milestone XProtect Empfehlung", MARGIN_X, 96);

  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...SLATE);
  doc.text("Xortec GmbH - Distribution mit Loesungskompetenz", MARGIN_X, 112);

  // Projekt / Datum Box
  doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
  doc.roundedRect(MARGIN_X, 128, contentWidth, 40, 4, 4, "S");
  doc.setFontSize(9); doc.setTextColor(...MUTED);
  doc.text("PROJEKT", MARGIN_X + 12, 144);
  doc.text("DATUM", MARGIN_X + contentWidth / 2, 144);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...DARK);
  doc.text(ascii(projectName || "Ohne Namen"), MARGIN_X + 12, 160);
  doc.text(new Date().toLocaleDateString("de-DE"), MARGIN_X + contentWidth / 2, 160);

  let y = 190;

  // Empfehlung Box
  const rec = recommendation?.recommendedEdition;
  const recLabel = recommendation?.recommendedEditionLabel || EDITION_LABELS[rec] || "-";
  const recScore = recommendation?.scores?.[rec] || 0;
  doc.setFillColor(...ACCENT_SOFT);
  doc.roundedRect(MARGIN_X, y, contentWidth, 62, 6, 6, "F");
  doc.setFillColor(...ACCENT);
  doc.rect(MARGIN_X, y, 4, 62, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...ACCENT);
  doc.text("EMPFEHLUNG", MARGIN_X + 14, y + 20);
  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...DARK);
  doc.text(ascii(recLabel), MARGIN_X + 14, y + 44);
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(...ACCENT);
  doc.text(`Eignung: ${recScore}%`, pageWidth - MARGIN_X - 14, y + 44, { align: "right" });
  y += 82;

  // Eignung Tabelle
  y = sectionTitle(doc, "Eignung je Edition", y);
  autoTable(doc, {
    startY: y,
    head: [["Edition", "Eignung", "Balken"]],
    body: Object.entries(recommendation?.scores || {}).map(([k, v]) => [
      ascii(EDITION_LABELS[k] || k),
      `${v}%`,
      { pct: v, isRec: k === rec },
    ]),
    theme: "grid",
    headStyles: { fillColor: ACCENT, textColor: 255, fontStyle: "bold", fontSize: 9 },
    styles: { fontSize: 10, cellPadding: 6, textColor: DARK, lineColor: BORDER, lineWidth: 0.4 },
    columnStyles: {
      0: { cellWidth: 180 },
      1: { cellWidth: 60, halign: "right", fontStyle: "bold" },
      2: { cellWidth: contentWidth - 240 },
    },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didParseCell: (data) => { if (data.column.index === 2 && data.cell.section === "body") data.cell.text = []; },
    didDrawCell: (data) => {
      if (data.column.index === 2 && data.cell.section === "body" && data.cell.raw && typeof data.cell.raw === "object") {
        const { x, y: cy, width, height } = data.cell;
        const pct = Math.max(0, Math.min(100, data.cell.raw.pct));
        const barY = cy + height / 2 - 3;
        const barW = width - 12;
        doc.setFillColor(240, 244, 248);
        doc.roundedRect(x + 6, barY, barW, 6, 3, 3, "F");
        if (pct > 0) {
          doc.setFillColor(...(data.cell.raw.isRec ? ACCENT : [140, 160, 180]));
          doc.roundedRect(x + 6, barY, (barW * pct) / 100, 6, 3, 3, "F");
        }
      }
    },
  });
  y = doc.lastAutoTable.finalY + 16;

  // Ihre Angaben
  if (answers && questions) {
    y = sectionTitle(doc, "Ihre Angaben", y);
    autoTable(doc, {
      startY: y,
      head: [["Frage", "Antwort"]],
      body: questions.map((q) => {
        const v = answers[q.key];
        const exact = q.exactField ? answers[q.exactField] : null;
        return [ascii(q.label), ascii(formatAnswer(q, v, exact))];
      }),
      theme: "striped",
      headStyles: { fillColor: [40, 50, 66], textColor: 255, fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 5, textColor: DARK, lineColor: BORDER, lineWidth: 0.3 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.62, textColor: SLATE },
        1: { cellWidth: contentWidth * 0.38, fontStyle: "bold" },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // Warum diese Edition
  if (recommendation?.reasonsFor?.length) {
    y = sectionTitle(doc, "Warum diese Edition?", y);
    autoTable(doc, {
      startY: y,
      body: recommendation.reasonsFor.map((r) => ["+", ascii(r)]),
      theme: "plain",
      styles: { fontSize: 10, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 }, textColor: DARK, valign: "top" },
      columnStyles: {
        0: { cellWidth: 16, textColor: [46, 160, 100], fontStyle: "bold", halign: "center" },
        1: { cellWidth: contentWidth - 16 },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // Warum keine kleinere Edition
  const smaller = recommendation?.reasonsAgainstSmaller || {};
  const smallerEntries = Object.entries(smaller);
  if (smallerEntries.length) {
    y = sectionTitle(doc, "Warum keine kleinere Edition?", y);
    for (const [ed, reasons] of smallerEntries) {
      y = ensureSpace(doc, y, 30);
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...DARK);
      doc.text(ascii(EDITION_LABELS[ed] || ed), MARGIN_X, y);
      y += 4;
      autoTable(doc, {
        startY: y,
        body: reasons.map((r) => ["x", ascii(r)]),
        theme: "plain",
        styles: { fontSize: 9, cellPadding: { top: 2, bottom: 2, left: 4, right: 4 }, textColor: SLATE, valign: "top" },
        columnStyles: {
          0: { cellWidth: 14, textColor: [200, 60, 60], fontStyle: "bold", halign: "center" },
          1: { cellWidth: contentWidth - 24 },
        },
        margin: { left: MARGIN_X + 10, right: MARGIN_X },
      });
      y = doc.lastAutoTable.finalY + 8;
    }
  }

  // Warum keine groessere Edition
  const bigger = recommendation?.reasonsAgainstBigger || {};
  const biggerEntries = Object.entries(bigger);
  if (biggerEntries.length) {
    y = sectionTitle(doc, "Warum keine groessere Edition?", y);
    autoTable(doc, {
      startY: y,
      body: biggerEntries.map(([ed, text]) => [ascii(EDITION_LABELS[ed] || ed), ascii(text)]),
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 6, textColor: DARK, lineColor: BORDER, lineWidth: 0.3, valign: "top" },
      columnStyles: {
        0: { cellWidth: 140, fontStyle: "bold", fillColor: [248, 250, 252], textColor: ACCENT },
        1: { cellWidth: contentWidth - 140, textColor: SLATE },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // Artikel
  if (cart && cart.length) {
    y = sectionTitle(doc, "Empfohlene Milestone-Artikel", y);
    autoTable(doc, {
      startY: y,
      head: [["Xortec-Nr.", "Hersteller-Nr.", "Bezeichnung", "Menge"]],
      body: cart.map((it) => [
        it.xortecNo || "-",
        ascii(it.manufacturerNo || "-"),
        ascii(`${it.name || it.manufacturerNo || ""}${it.description ? "\n" + it.description : ""}`),
        String(it.qty || 1),
      ]),
      theme: "grid",
      headStyles: { fillColor: ACCENT, textColor: 255, fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 6, textColor: DARK, lineColor: BORDER, lineWidth: 0.3, valign: "middle" },
      columnStyles: {
        0: { cellWidth: 70, fontStyle: "bold", textColor: ACCENT },
        1: { cellWidth: 100 },
        3: { cellWidth: 50, halign: "right", fontStyle: "bold" },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
    });
    y = doc.lastAutoTable.finalY + 8;
    y = ensureSpace(doc, y, 20);
    doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(...MUTED);
    doc.text("Preise auf Anfrage bei der Xortec GmbH. Alle Artikel werden digital ausgeliefert (E-Lizenzen).", MARGIN_X, y);
    y += 18;
  }

  // Konfigurationscode
  if (configCode) {
    y = ensureSpace(doc, y, 70);
    doc.setFillColor(...ACCENT_SOFT);
    doc.roundedRect(MARGIN_X, y, contentWidth, 56, 6, 6, "F");
    doc.setFillColor(...ACCENT); doc.rect(MARGIN_X, y, 4, 56, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...ACCENT);
    doc.text("KONFIGURATIONSCODE", MARGIN_X + 14, y + 18);
    doc.setFont("courier", "bold"); doc.setFontSize(22); doc.setTextColor(...DARK);
    doc.text(ascii(configCode), MARGIN_X + 14, y + 42);
    doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(...MUTED);
    doc.text("Mit diesem Code oeffnen Sie das Projekt spaeter erneut im Auswahlassistenten.", pageWidth - MARGIN_X - 14, y + 42, { align: "right" });
    y += 66;
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.4);
    doc.line(MARGIN_X, 810, pageWidth - MARGIN_X, 810);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED);
    doc.text("Milestone XProtect Auswahlassistent - Xortec GmbH", MARGIN_X, 823);
    doc.text(`Seite ${i} von ${pageCount}`, pageWidth - MARGIN_X, 823, { align: "right" });
  }

  const safeName = (projectName || "XProtect_Empfehlung").replace(/[^a-zA-Z0-9-_ ]/g, "_");
  doc.save(`${safeName}.pdf`);
}

// ---------------------------------------------------------------------------
// PDF fuer den Konfigurationsmodus (freie Lizenz-/Care-Auswahl)
// ---------------------------------------------------------------------------
const EDITION_SHORT = { express: "Express+", professional: "Professional+", expert: "Expert", corporate: "Corporate" };

export async function exportConfigPdf({ projectName, cart, summary, configCode }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN_X * 2;

  // Header
  const logo = await loadLogo();
  if (logo) {
    try { doc.addImage(logo, "PNG", MARGIN_X, 30, 100, 28, undefined, "FAST"); } catch {}
  } else {
    doc.setFillColor(...ACCENT); doc.rect(MARGIN_X, 30, 80, 28, "F");
    doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
    doc.text("XORTEC", MARGIN_X + 40, 49, { align: "center" });
  }
  doc.setDrawColor(...ACCENT); doc.setLineWidth(2);
  doc.line(MARGIN_X, 70, pageWidth - MARGIN_X, 70);

  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...DARK);
  doc.text("Milestone XProtect Lizenz-Konfiguration", MARGIN_X, 96);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...SLATE);
  doc.text("Xortec GmbH - Distribution mit Loesungskompetenz", MARGIN_X, 112);

  // Projekt / Datum Box
  doc.setDrawColor(...BORDER); doc.setLineWidth(0.5);
  doc.roundedRect(MARGIN_X, 128, contentWidth, 40, 4, 4, "S");
  doc.setFontSize(9); doc.setTextColor(...MUTED);
  doc.text("PROJEKT", MARGIN_X + 12, 144);
  doc.text("DATUM", MARGIN_X + contentWidth / 2, 144);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...DARK);
  doc.text(ascii(projectName || "Ohne Namen"), MARGIN_X + 12, 160);
  doc.text(new Date().toLocaleDateString("de-DE"), MARGIN_X + contentWidth / 2, 160);

  let y = 190;

  // Kennzahlen-Box
  const totals = summary?.totals || { editions: [], devices: 0, care: 0, positions: 0 };
  const editionsLabel = (totals.editions || []).map((e) => EDITION_SHORT[e] || e).join(", ") || "-";
  doc.setFillColor(...ACCENT_SOFT);
  doc.roundedRect(MARGIN_X, y, contentWidth, 62, 6, 6, "F");
  doc.setFillColor(...ACCENT); doc.rect(MARGIN_X, y, 4, 62, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...ACCENT);
  doc.text("KONFIGURATION", MARGIN_X + 14, y + 20);
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(...DARK);
  doc.text(ascii(`XProtect ${editionsLabel}`), MARGIN_X + 14, y + 44);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...ACCENT);
  doc.text(
    `${totals.devices} Device-Lizenzen  |  ${totals.care} Care-Lizenzen  |  ${totals.positions} Positionen`,
    pageWidth - MARGIN_X - 14, y + 44, { align: "right" }
  );
  y += 82;

  // Uebersicht je Edition
  const per = summary?.perEdition || {};
  const perEntries = Object.entries(per);
  if (perEntries.length) {
    y = sectionTitle(doc, "Uebersicht je Edition", y);
    autoTable(doc, {
      startY: y,
      head: [["Edition", "Basis-Lizenz", "Device-Lizenzen", "Care Plus", "Care Premium"]],
      body: perEntries.map(([ed, s]) => [
        ascii(EDITION_LABELS[ed] || ed), String(s.base), String(s.devices), String(s.carePlus), String(s.carePremium),
      ]),
      theme: "grid",
      headStyles: { fillColor: ACCENT, textColor: 255, fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 10, cellPadding: 6, textColor: DARK, lineColor: BORDER, lineWidth: 0.4 },
      columnStyles: {
        0: { fontStyle: "bold" },
        1: { halign: "right" }, 2: { halign: "right", fontStyle: "bold" }, 3: { halign: "right" }, 4: { halign: "right" },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // Positionen
  if (cart && cart.length) {
    y = sectionTitle(doc, "Milestone-Artikel", y);
    autoTable(doc, {
      startY: y,
      head: [["Pos.", "Xortec-Nr.", "Hersteller-Nr.", "Bezeichnung", "Menge"]],
      body: cart.map((it, idx) => [
        String(idx + 1),
        it.xortecNo || "-",
        ascii(it.manufacturerNo || "-"),
        ascii(`${it.name || it.manufacturerNo || ""}\n${EDITION_LABELS[it.edition] || ""}${it.description ? " - " + it.description : ""}`),
        String(it.qty || 1),
      ]),
      theme: "grid",
      headStyles: { fillColor: ACCENT, textColor: 255, fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 6, textColor: DARK, lineColor: BORDER, lineWidth: 0.3, valign: "middle" },
      columnStyles: {
        0: { cellWidth: 32, halign: "center", textColor: MUTED },
        1: { cellWidth: 68, fontStyle: "bold", textColor: ACCENT },
        2: { cellWidth: 110 },
        4: { cellWidth: 46, halign: "right", fontStyle: "bold" },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
    });
    y = doc.lastAutoTable.finalY + 8;
    y = ensureSpace(doc, y, 20);
    doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(...MUTED);
    doc.text("Preise auf Anfrage bei der Xortec GmbH. Alle Artikel werden digital ausgeliefert (E-Lizenzen).", MARGIN_X, y);
    y += 18;
  }

  // Hinweise
  const warnings = summary?.warnings || [];
  if (warnings.length) {
    y = sectionTitle(doc, "Hinweise zur Konfiguration", y);
    const mark = { error: "!", warning: "!", info: "i" };
    const color = { error: [200, 60, 60], warning: [200, 140, 40], info: ACCENT };
    autoTable(doc, {
      startY: y,
      body: warnings.map((w) => [{ content: mark[w.level] || "i", styles: { textColor: color[w.level] || ACCENT } }, ascii(w.text)]),
      theme: "plain",
      styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 }, textColor: SLATE, valign: "top" },
      columnStyles: {
        0: { cellWidth: 16, fontStyle: "bold", halign: "center" },
        1: { cellWidth: contentWidth - 16 },
      },
      margin: { left: MARGIN_X, right: MARGIN_X },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // Konfigurationscode
  if (configCode) {
    y = ensureSpace(doc, y, 70);
    doc.setFillColor(...ACCENT_SOFT);
    doc.roundedRect(MARGIN_X, y, contentWidth, 56, 6, 6, "F");
    doc.setFillColor(...ACCENT); doc.rect(MARGIN_X, y, 4, 56, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...ACCENT);
    doc.text("KONFIGURATIONSCODE", MARGIN_X + 14, y + 18);
    doc.setFont("courier", "bold"); doc.setFontSize(22); doc.setTextColor(...DARK);
    doc.text(ascii(configCode), MARGIN_X + 14, y + 42);
    doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(...MUTED);
    doc.text("Mit diesem Code oeffnen Sie die Konfiguration spaeter erneut im Konfigurationsmodus.", pageWidth - MARGIN_X - 14, y + 42, { align: "right" });
    y += 66;
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.4);
    doc.line(MARGIN_X, 810, pageWidth - MARGIN_X, 810);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED);
    doc.text("Milestone XProtect Konfigurator - Xortec GmbH", MARGIN_X, 823);
    doc.text(`Seite ${i} von ${pageCount}`, pageWidth - MARGIN_X, 823, { align: "right" });
  }

  const safeName = (projectName || "XProtect_Konfiguration").replace(/[^a-zA-Z0-9-_ ]/g, "_");
  doc.save(`${safeName}.pdf`);
}

function formatAnswer(q, v, exact) {
  if (exact && Number(exact) > 0) return `${exact} Kameras (exakt)`;
  if (v === undefined || v === null || v === "") return "-";
  if (typeof v === "boolean") return v ? "Ja" : "Nein";
  if (q.options) {
    const opt = q.options.find((o) => o.value === v);
    if (opt) return opt.label;
  }
  if (typeof v === "object") {
    return Object.entries(v).filter(([, val]) => val).map(([k]) => k).join(", ") || "-";
  }
  return String(v);
}
